import type { Supplier } from '../interfaces/procurement/supplier.interface';

const FORBIDDEN_MESSAGE =
  'You do not have permission to view suppliers. Ask an administrator to grant the Supplier View permission.';
const FALLBACK_MESSAGE = 'Failed to load suppliers';

type ApiErrorShape = { response?: { status?: number; data?: unknown } };

/** The server's own message for a non-403 failure (string body, or `{ message | error }`), so a
 *  real outage ("server unavailable", a 500's text) is not flattened into the generic fallback. */
function serverMessage(data: unknown): string | undefined {
  if (typeof data === 'string') {
    const trimmed = data.trim();
    // An HTML error page (proxy 502/504) is not a user-facing message.
    return trimmed && !/^<(!doctype|html|head|body)/i.test(trimmed) ? trimmed : undefined;
  }
  if (data && typeof data === 'object') {
    const { message, error } = data as { message?: unknown; error?: unknown };
    if (typeof message === 'string' && message) return message;
    if (typeof error === 'string' && error) return error;
  }
  return undefined;
}

/** Runs a supplier-list fetch that must never break its host form: on failure returns [] plus a
 *  user-facing message (a clear permission hint for 403, the server's message otherwise). */
export async function loadSuppliersSafely(
  fetcher: () => Promise<{ content?: Supplier[] }>,
): Promise<{ suppliers: Supplier[]; error: string | null; forbidden: boolean }> {
  try {
    const res = await fetcher();
    return { suppliers: res?.content ?? [], error: null, forbidden: false };
  } catch (e) {
    const response = (e as ApiErrorShape)?.response;
    if (response?.status === 403) {
      return { suppliers: [], error: FORBIDDEN_MESSAGE, forbidden: true };
    }
    return {
      suppliers: [],
      error: serverMessage(response?.data) ?? FALLBACK_MESSAGE,
      forbidden: false,
    };
  }
}
